import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

const Auth = () => {
	usePageTitle("Sign In");
	const { signIn, forgotPassword, loading, user } = useAuth();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [redirecting, setRedirecting] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [isSendingReset, setIsSendingReset] = useState(false);
	const { t } = useTranslation();

	// Redirect to dashboard if already logged in
	useEffect(() => {
		if (user && !loading && !redirecting) {
			setRedirecting(true);
			navigate("/");
		}
	}, [user, loading, navigate, redirecting]);

	// If redirecting, don't render anything to prevent flashes
	if (redirecting) {
		return null;
	}

	const handleSignIn = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			// Use password sign in instead of provider
			const result = await signIn(email, password);
			
			if (!result.error) {
				toast({
					title: t('common.success'),
					description: t('auth.signInSuccess'),
				});
			}
		} catch (error) {
			toast({
				title: t('common.error'),
				description: error instanceof Error ? error.message : t('auth.signInError'),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleForgotPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!forgotEmail.trim()) {
			toast({
				title: t('common.error'),
				description: t('errors.invalidEmail'),
				variant: "destructive",
			});
			return;
		}

		setIsSendingReset(true);

		try {
			const result = await forgotPassword(forgotEmail);
			
			if (!result.error) {
				setShowForgotPassword(false);
				setForgotEmail("");
				// Success toast is already shown by the forgotPassword method
			}
		} catch (error) {
			toast({
				title: t('common.error'),
				description: error instanceof Error ? error.message : 'Failed to send reset email',
				variant: "destructive",
			});
		} finally {
			setIsSendingReset(false);
		}
	};
	return (
		<div className="flex min-h-screen items-center justify-center bg-digger-light-gray p-4">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="mx-auto mb-4 flex items-center justify-center">
						<div className="flex items-center gap-3">
							<div className="w-16 h-16 md:w-20 md:h-20 rounded-sm flex items-center justify-center overflow-hidden">
								<img src="/img/NewDiggerIcon.png" alt="Digger" className="w-full h-full object-contain" />
							</div>
							<div className="flex items-baseline gap-1">
								<span className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
									digger
								</span>
								<span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#5a7ebf] inline-block" />
							</div>
						</div>
					</div>
					<CardTitle className="text-2xl">
						{showForgotPassword ? t('auth.resetPassword') : t('auth.welcome')}
					</CardTitle>
					<CardDescription>
						{showForgotPassword 
							? 'Enter your email to receive a password reset link'
							: t('auth.platformDescription')
						}
					</CardDescription>
				</CardHeader>
				
				{!showForgotPassword ? (
					<form onSubmit={handleSignIn}>
						<CardContent className="space-y-4 pt-6">
							<div className="space-y-2">
								<label htmlFor="email" className="text-sm font-medium">
									{t('common.email')}
								</label>
								<Input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder={t('auth.emailPlaceholder')}
									required
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor="password" className="text-sm font-medium">
									{t('common.password')}
								</label>
								<Input
									id="password"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="••••••••"
									required
								/>
							</div>
						</CardContent>
						<CardFooter className="flex flex-col space-y-2">
							<Button 
								type="submit" 
								className="w-full bg-digger-blue hover:bg-digger-blue-light"
								disabled={isLoading}
							>
								{isLoading ? t('auth.signingIn') : t('common.login')}
							</Button>
							<Button 
								variant="link" 
								type="button"
								className="text-sm text-digger-blue"
								onClick={() => setShowForgotPassword(true)}
							>
								{t('auth.forgotPassword')}
							</Button>
						</CardFooter>
					</form>
				) : (
					<form onSubmit={handleForgotPassword}>
						<CardContent className="space-y-4 pt-6">
							<div className="space-y-2">
								<label htmlFor="forgotEmail" className="text-sm font-medium">
									{t('common.email')}
								</label>
								<Input
									id="forgotEmail"
									type="email"
									value={forgotEmail}
									onChange={(e) => setForgotEmail(e.target.value)}
									placeholder={t('auth.emailPlaceholder')}
									required
								/>
							</div>
						</CardContent>
						<CardFooter className="flex flex-col space-y-2">
							<Button 
								type="submit" 
								className="w-full bg-digger-blue hover:bg-digger-blue-light"
								disabled={isSendingReset}
							>
								{isSendingReset ? t('auth.sendingResetEmail') : t('auth.sendResetEmail')}
							</Button>
							<Button 
								variant="link" 
								type="button"
								className="text-sm text-digger-blue"
								onClick={() => setShowForgotPassword(false)}
							>
								{t('auth.backToLogin')}
							</Button>
						</CardFooter>
					</form>
				)}
			</Card>
		</div>
	);
};

export default Auth;
